import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { BookOpen, Sparkles, Star, Zap } from 'lucide-react';

const LOGO_URL = 'https://yourpersonalshopper-storage.sfo3.digitaloceanspaces.com/omniread/omniread_logo.webp';

export default function Landing({ user }) {
  const navigate = useNavigate();

  const handleLogin = () => {
    navigate('/login');
  };

  if (user) {
    navigate('/dashboard');
    return null;
  }

  return (
    <div className="page-container flex flex-col items-center justify-center min-h-screen p-4 md:p-12 overflow-x-hidden">
      <div className="fade-in max-w-5xl w-full text-center px-2">
        {/* Logo */}
        <div className="mb-8 md:mb-12">
          <img
            src={LOGO_URL}
            alt="OmniRead.AI"
            className="w-32 h-32 md:w-48 md:h-48 mx-auto block drop-shadow-2xl"
          />
        </div>

        <h1 className="gold-text mb-4 text-3xl sm:text-4xl md:text-7xl font-bold tracking-tighter break-words uppercase">
          OMNIREAD.AI
        </h1>

        <p className="text-lg md:text-2xl text-[#d4af37] italic mb-8 font-serif leading-relaxed tracking-wide px-4">
          A devotional written just for you across every tradition
        </p>

        <p className="text-lg md:text-xl leading-relaxed text-[#c9c9c9] max-w-2xl mx-auto mb-12">
          Enter any topic and receive a personalized reflection written across multiple traditions—psychology,
          world religions, myth, and philosophy. Quiet, personal, meaningful.
        </p>

        <Button
          onClick={handleLogin}
          data-testid="login-button"
          className="btn-gold text-lg md:text-xl px-10 py-4 md:px-16 h-auto"
        >
          Begin Your Journey
        </Button>

        {/* Features */}
        <div className="mystical-card mt-16 md:mt-24">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12">
            <div className="text-center group">
              <Sparkles size={48} className="text-[#d4af37] mx-auto mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl md:text-2xl mb-2 font-serif text-[#d4af37]">Cross-Cultural Wisdom</h3>
              <p className="text-gray-400 text-sm md:text-base leading-relaxed">12+ wisdom lenses from Jungian to Taoist, Greek to Sufi</p>
            </div>

            <div className="text-center group">
              <BookOpen size={48} className="text-[#d4af37] mx-auto mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl md:text-2xl mb-2 font-serif text-[#d4af37]">Daily Devotionals</h3>
              <p className="text-gray-400 text-sm md:text-base leading-relaxed">AI-generated passages tailored to your life themes</p>
            </div>

            <div className="text-center group">
              <Star size={48} className="text-[#d4af37] mx-auto mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl md:text-2xl mb-2 font-serif text-[#d4af37]">Track Your Growth</h3>
              <p className="text-gray-400 text-sm md:text-base leading-relaxed">Earn badges, build streaks, save your favorite passages</p>
            </div>
          </div>
        </div>

        <p className="text-center mt-12 md:mt-16 text-lg md:text-xl text-[#d4af37] font-serif italic tracking-wide">
          Where myth meets mind, where wisdom converges
        </p>

        {/* Disclaimer and Copyright */}
        <div className="mt-16 md:mt-24 pt-8 border-t border-[#d4af37]/20 w-full max-w-4xl mx-auto">
          <p className="text-xs text-[#999] leading-relaxed max-w-3xl mx-auto mb-6 px-4">
            OmniRead offers multiple perspectives, not absolute truth. Information may be incomplete, evolving, or interpreted differently depending on context. Use OmniRead as a tool for reflection, exploration, and insight — not as a single source of authority.
          </p>
          <p className="text-sm text-[#666] font-medium">
            © {new Date().getFullYear()} OmniRead.AI. All rights reserved. •{' '}
            <Link to="/privacy" className="hover:text-[#d4af37] transition-colors">
              Privacy Policy
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}